import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { useMakeMap } from '../../hooks/useMakeMap.js';
import { LoadingSpinner } from '../ui/index.js';
import type { Day, DayScene, Pin } from '../../types/index.js';



const PIN_EMOJI: Record<string, string> = {
  main: '📍', basecamp: '🏠', parking: '🅿️', hospital: '🏥',
};

export function DayTray() {
  const { activeProject, flyTo, setDetailPin, mapMode, setMapMode } = useMapStore();
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);
  const [expandedSceneId, setExpandedSceneId] = useState<string | null>(null);
  const autoSelected = useRef(false);
  const makeMap = useMakeMap();

  // Reset when project changes
  useEffect(() => {
    setSelectedDayId(null);
    setExpandedSceneId(null);
    autoSelected.current = false;
  }, [activeProject?.id]);

  const { data: days = [] } = useQuery({
    queryKey: ['days', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];
      const result = await api.get<{ days: Day[] }>(`/days?projectId=${activeProject.id}`);
      return result.success ? result.data.days : [];
    },
    enabled: !!activeProject,
  });

  // Auto-select first day on initial load
  useEffect(() => {
    if (days.length > 0 && !autoSelected.current) {
      autoSelected.current = true;
      setSelectedDayId(days[0].id);
    }
  }, [days]);

  const { data: dayScenes = [], isLoading: scenesLoading } = useQuery({
    queryKey: ['day-scenes', selectedDayId],
    queryFn: async () => {
      const result = await api.get<DayScene[]>(`/days/${selectedDayId}/scenes`);
      return result.success ? result.data : [];
    },
    enabled: !!selectedDayId,
  });

  if (!activeProject) return null;

  return (
    <div
      className="absolute bottom-4 right-4 z-20 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col"
      style={{ maxHeight: '420px' }}
    >
      {/* Day dropdown + Make Map / Exit Map Mode */}
      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            {days.length === 0 ? (
              <p className="text-sm text-gray-400">No days in this project</p>
            ) : (
              <select
                value={selectedDayId ?? ''}
                onChange={e => {
                  setSelectedDayId(e.target.value || null);
                  setExpandedSceneId(null);
                }}
                className="w-full text-sm font-semibold text-gray-800 bg-transparent border-none focus:outline-none cursor-pointer"
              >
                <option value="">— Select a day —</option>
                {days.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.shootDate ? ` · ${d.shootDate}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {mapMode ? (
            <button
              onClick={() => setMapMode(false)}
              title="Exit map mode"
              className="shrink-0 text-xs px-2 py-1 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
            >✕ Map</button>
          ) : selectedDayId ? (
            <button
              onClick={() => {
                const day = days.find(d => d.id === selectedDayId);
                if (day) makeMap(day);
              }}
              title="Make Map — fit view to this day's locations"
              className="shrink-0 text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
            >🗺️ Map</button>
          ) : null}
        </div>
      </div>

      {/* Scene list */}
      <div className="overflow-y-auto flex-1 p-2 space-y-1">
        {!selectedDayId ? (
          <p className="text-xs text-gray-400 text-center py-4">Pick a day above</p>
        ) : scenesLoading ? (
          <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
        ) : dayScenes.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No scenes scheduled for this day</p>
        ) : (
          dayScenes.map((ds, i) => (
            <TraySceneRow
              key={ds.id}
              dayScene={ds}
              index={i}
              isExpanded={expandedSceneId === ds.sceneId}
              onToggle={() => setExpandedSceneId(expandedSceneId === ds.sceneId ? null : ds.sceneId)}
              onFly={() => { if (ds.lat && ds.lng) flyTo(ds.lng as number, ds.lat as number); }}
              onPinClick={pin => { setDetailPin(pin); flyTo(pin.lng, pin.lat); }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TraySceneRow({
  dayScene, index, isExpanded, onToggle, onFly, onPinClick,
}: {
  dayScene: DayScene;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onFly: () => void;
  onPinClick: (pin: Pin) => void;
}) {
  const { data: pins = [], isLoading } = useQuery({
    queryKey: ['pins', dayScene.sceneId],
    queryFn: async () => {
      const result = await api.get<{ pins: Pin[] }>(`/pins?sceneId=${dayScene.sceneId}&limit=200`);
      return result.success ? result.data.pins : [];
    },
    enabled: isExpanded,
  });

  return (
    <div className={`rounded-xl border transition-colors ${isExpanded ? 'border-brand-200 bg-brand-50/30' : 'border-gray-100'}`}>
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50 rounded-xl"
        onClick={onToggle}
      >
        <span className="text-xs font-medium text-gray-400 w-4 shrink-0">{index + 1}</span>
        <p className="flex-1 text-sm font-semibold text-gray-800 truncate">{dayScene.sceneName ?? 'Scene'}</p>
        {dayScene.departureTime && (
          <span className="text-xs text-gray-400 shrink-0">🕐 {dayScene.departureTime}</span>
        )}
        <button
          onClick={e => { e.stopPropagation(); onFly(); }}
          className="text-gray-400 hover:text-brand-500 text-xs p-0.5 shrink-0"
          title="Fly to"
        >🎯</button>
        <span className="text-gray-300 text-xs shrink-0">{isExpanded ? '▲' : '▼'}</span>
      </div>

      {isExpanded && (
        <div className="border-t border-brand-100 px-3 py-2.5 space-y-2 rounded-b-xl">
          {isLoading ? (
            <div className="flex justify-center py-2"><LoadingSpinner size="sm" /></div>
          ) : pins.length === 0 ? (
            <p className="text-xs text-gray-400">No locations added to this scene</p>
          ) : (
            pins.map(pin => (
              <div
                key={pin.id}
                className="flex items-center gap-2 cursor-pointer hover:bg-white rounded-lg px-2 py-1 -mx-2 transition-colors group"
                onClick={() => onPinClick(pin)}
                title="View details"
              >
                <span className="text-sm shrink-0">{PIN_EMOJI[pin.pinType] ?? '📍'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate group-hover:text-brand-600">{pin.title}</p>
                  {pin.address && <p className="text-xs text-gray-400 truncate">{pin.address}</p>}
                </div>
                <span className="text-gray-300 text-xs opacity-0 group-hover:opacity-100 shrink-0">›</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
